import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCircleByInviteCode, joinByInviteCode } from '../services/circleService';
import { useCircle } from '../hooks/useCircle';
import SupervisedChildLoginModal from '../components/SupervisedChildLoginModal';

export default function LoginPage({ showOnboardingMessage = false }) {
  const { signInWithGoogle, login, signup, resetPassword, sendEmailLink, currentUser, userProfile, signInAnonymous } = useAuth();
  const navigate = useNavigate();
  const { selectCircle } = useCircle();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('buttons'); // 'buttons', 'email', 'password', 'joincode'
  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: ''
  });
  const [emailLinkSent, setEmailLinkSent] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [circle, setCircle] = useState(null);
  const [joinStatus, setJoinStatus] = useState('idle'); // 'idle', 'validating', 'preview', 'joining'
  const [guestName, setGuestName] = useState('');
  const [showChildLogin, setShowChildLogin] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordAuth = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
      } else {
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        await signup(formData.email, formData.password, formData.displayName);
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLink = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await sendEmailLink(formData.email);
      setEmailLinkSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send sign-in link');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setView('buttons');
    setError(null);
  };

  const backToButtons = () => {
    setView('buttons');
    setError(null);
    setJoinStatus('idle');
    setCircle(null);
    setInviteCode('');
  };

  const handleValidateCode = async () => {
    if (!inviteCode.trim()) return;
    setError(null);
    setJoinStatus('validating');
    setLoading(true);
    try {
      const c = await getCircleByInviteCode(inviteCode.trim());
      setCircle(c);
      setJoinStatus('preview');
    } catch (err) {
      setError(err.message || 'Invalid invite code');
      setJoinStatus('idle');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWithAuth = async () => {
    setJoinStatus('joining');
    setLoading(true);
    try {
      const circleId = await joinByInviteCode(inviteCode.trim(), currentUser.uid, userProfile);
      selectCircle(circleId);
      navigate('/art');
    } catch (err) {
      if (err.message?.includes('already')) {
        navigate('/art');
      } else {
        setError(err.message);
        setJoinStatus('preview');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleJoinAsGuest = async () => {
    const displayName = guestName.trim() || 'Guest Artist';
    setJoinStatus('joining');
    setLoading(true);
    try {
      const user = await signInAnonymous();
      const profile = {
        displayName,
        isAnonymous: true,
      };
      const circleId = await joinByInviteCode(inviteCode.trim(), user.uid, profile);
      selectCircle(circleId);
      navigate('/art');
    } catch (err) {
      setError(err.message);
      setJoinStatus('preview');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (showOnboardingMessage) return 'Get Started';
    if (view === 'email') return 'Sign In with Email';
    if (view === 'joincode') {
      if (joinStatus === 'preview') return 'Join Circle';
      return 'Have an invite code?';
    }
    if (view === 'password') {
      return mode === 'login' ? 'Sign In with Password' : 'Create Account';
    }
    return mode === 'login' ? 'Sign In to Art Ping Pong' : 'Create Account';
  };

  const getSubtitle = () => {
    if (showOnboardingMessage) return 'Create or join a circle to start making art together.';
    if (view === 'email') return 'Enter your email to receive a secure sign-in link.';
    if (view === 'joincode') {
      if (joinStatus === 'preview') return `Join ${circle?.name || 'this circle'} to start creating together.`;
      return 'Enter your 6-character invite code to join a circle.';
    }
    if (view === 'password') {
      return mode === 'login' 
        ? 'Sign in with your email and password.'
        : 'Create a new account with email and password.';
    }
    return 'Login to save your progress.';
  };

  return (
    <Container>
      <Hero
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <LogoImage src="/AppLogoStacked.svg" alt="Art Ping Pong" />
        <Tagline>Create art together with your family</Tagline>
      </Hero>

      <Card
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <CardHeader>
          <Title>{getTitle()}</Title>
          <Subtitle>{getSubtitle()}</Subtitle>
          {showOnboardingMessage && (
            <OnboardingNote>
              Free plan includes 5 AI creations and 20 drawings per month
            </OnboardingNote>
          )}
        </CardHeader>

        <CardBody>
          {view === 'buttons' && (
            <>
              <MethodButton onClick={handleGoogleSignIn} disabled={loading}>
                <GoogleIcon />
                Continue with Google
              </MethodButton>

              <MethodButton onClick={() => setView('email')} disabled={loading}>
                <MailIcon />
                Continue with Email
              </MethodButton>

              <MethodButton onClick={() => setView('password')} disabled={loading}>
                <LockIcon />
                Sign In with Password
              </MethodButton>

              <PinButton onClick={() => setShowChildLogin(true)} disabled={loading}>
                <ChildIcon />
                Sign In with PIN (for kids)
              </PinButton>

              <Divider>
                <DividerLine />
                <DividerText>or</DividerText>
                <DividerLine />
              </Divider>

              <JoinCodeButton onClick={() => setView('joincode')} disabled={loading}>
                <TicketIcon />
                Join with Invite Code
              </JoinCodeButton>

              <ToggleText>
                {mode === 'login' ? (
                  <>
                    Don't have an account?{' '}
                    <ToggleLink onClick={toggleMode}>Sign Up</ToggleLink>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <ToggleLink onClick={toggleMode}>Sign In</ToggleLink>
                  </>
                )}
              </ToggleText>
            </>
          )}

          {view === 'email' && (
            <>
              {emailLinkSent ? (
                <SuccessMessage>
                  <p>Check your email for a sign-in link!</p>
                  <BackLink onClick={backToButtons}>Back to Sign In Options</BackLink>
                </SuccessMessage>
              ) : (
                <Form onSubmit={handleEmailLink}>
                  <FormGroup>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your@email.com"
                    />
                  </FormGroup>

                  {error && <ErrorMessage>{error}</ErrorMessage>}

                  <SubmitButton type="submit" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Sign-In Link'}
                  </SubmitButton>

                  <BackLink onClick={backToButtons}>Back to Sign In Options</BackLink>
                </Form>
              )}

              <ToggleText>
                {mode === 'login' ? (
                  <>
                    Don't have an account?{' '}
                    <ToggleLink onClick={toggleMode}>Sign Up</ToggleLink>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <ToggleLink onClick={toggleMode}>Sign In</ToggleLink>
                  </>
                )}
              </ToggleText>
            </>
          )}

          {view === 'password' && (
            <>
              <Form onSubmit={handlePasswordAuth}>
                {mode === 'signup' && (
                  <FormGroup>
                    <Label htmlFor="displayName">Display Name</Label>
                    <Input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your name"
                    />
                  </FormGroup>
                )}

                <FormGroup>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your@email.com"
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    placeholder="••••••••"
                    minLength={mode === 'signup' ? 6 : undefined}
                  />
                </FormGroup>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <SubmitButton type="submit" disabled={loading}>
                  {loading ? 'Loading...' : mode === 'login' ? 'Sign In' : 'Create Account'}
                </SubmitButton>

                <BackLink onClick={backToButtons}>Back to Sign In Options</BackLink>
              </Form>

              <ToggleText>
                {mode === 'login' ? (
                  <>
                    Don't have an account?{' '}
                    <ToggleLink onClick={toggleMode}>Sign Up</ToggleLink>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <ToggleLink onClick={toggleMode}>Sign In</ToggleLink>
                  </>
                )}
              </ToggleText>
            </>
          )}

          {view === 'joincode' && (
            <>
              {joinStatus === 'idle' || joinStatus === 'validating' ? (
                <Form onSubmit={(e) => { e.preventDefault(); handleValidateCode(); }}>
                  <FormGroup>
                    <Label htmlFor="inviteCode">Invite Code</Label>
                    <Input
                      type="text"
                      id="inviteCode"
                      name="inviteCode"
                      value={inviteCode}
                      onChange={(e) => {
                        setInviteCode(e.target.value.toUpperCase());
                        setError(null);
                      }}
                      required
                      placeholder="ABC123"
                      maxLength={6}
                      style={{ textTransform: 'uppercase', textAlign: 'center', letterSpacing: '2px' }}
                    />
                  </FormGroup>

                  {error && <ErrorMessage>{error}</ErrorMessage>}

                  <SubmitButton type="submit" disabled={loading || !inviteCode.trim()}>
                    {loading ? 'Checking...' : 'Check Code'}
                  </SubmitButton>

                  <BackLink onClick={backToButtons}>Back to Sign In Options</BackLink>
                </Form>
              ) : joinStatus === 'preview' && circle ? (
                <>
                  <CirclePreviewCard>
                    <CircleName>{circle.name}</CircleName>
                    <CircleMeta>{circle.memberIds?.length || 1} member{(circle.memberIds?.length || 1) !== 1 ? 's' : ''}</CircleMeta>
                  </CirclePreviewCard>

                  {currentUser ? (
                    <SubmitButton onClick={handleJoinWithAuth} disabled={loading}>
                      {loading ? 'Joining...' : 'Join Circle'}
                    </SubmitButton>
                  ) : (
                    <>
                      <FormGroup>
                        <Label htmlFor="guestName">Your Name (optional)</Label>
                        <Input
                          type="text"
                          id="guestName"
                          name="guestName"
                          value={guestName}
                          onChange={(e) => setGuestName(e.target.value)}
                          placeholder="Guest Artist"
                          maxLength={30}
                        />
                      </FormGroup>
                      <SubmitButton onClick={handleJoinAsGuest} disabled={loading}>
                        {loading ? 'Joining...' : 'Join as Guest'}
                      </SubmitButton>
                      <OrText>or</OrText>
                      <SecondaryButton onClick={handleGoogleSignIn} disabled={loading}>
                        <GoogleIcon />
                        Sign In to Join
                      </SecondaryButton>
                    </>
                  )}

                  {error && <ErrorMessage style={{ marginTop: '16px' }}>{error}</ErrorMessage>}

                  <BackLink onClick={backToButtons}>Back to Sign In Options</BackLink>
                </>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>
      
      <LegalFooter>
        <LegalLink href="https://app.domoreworld.com/privacy" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </LegalLink>
        <LegalSeparator>•</LegalSeparator>
        <LegalLink href="https://app.domoreworld.com/terms" target="_blank" rel="noopener noreferrer">
          Terms of Service
        </LegalLink>
      </LegalFooter>

      <SupervisedChildLoginModal
        isOpen={showChildLogin}
        onClose={() => setShowChildLogin(false)}
        onSuccess={() => navigate('/art')}
      />
    </Container>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

function TicketIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

function ChildIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="4" />
      <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    </svg>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  padding: calc(40px + env(safe-area-inset-top, 0px)) calc(24px + env(safe-area-inset-right, 0px)) 40px calc(24px + env(safe-area-inset-left, 0px));
  background: linear-gradient(180deg, #fffbeb 0%, #ffffff 50%);
`;

const Hero = styled(motion.div)`
  text-align: center;
  margin-bottom: 32px;
`;

const LogoImage = styled.img`
  height: 120px;
  width: auto;
  display: block;
  margin: 0 auto 16px;

  @media (max-width: 640px) {
    height: 100px;
  }
`;

const Tagline = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 24px 24px 16px;
  text-align: center;
`;

const Title = styled.h2`
  margin: 0 0 8px;
  font-size: 24px;
  color: ${({ theme }) => theme.colors.text};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const OnboardingNote = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 12px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
`;

const CardBody = styled.div`
  padding: 0 24px 24px;
`;

const MethodButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  margin-bottom: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50px;
  background: white;
  color: ${({ theme }) => theme.colors.text};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  font-size: 16px;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryDark || theme.colors.primary};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  cursor: pointer;
  padding: 8px 0;
  text-align: center;
  width: 100%;
  margin-top: 8px;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    text-decoration: underline;
  }
`;

const ToggleText = styled.p`
  text-align: center;
  margin-top: 20px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
`;

const ToggleLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee;
  color: #c33;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #fcc;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 16px;
  background: #ecfdf5;
  border-radius: 8px;
  margin-top: 8px;

  p {
    color: #059669;
    margin: 0 0 16px;
    font-weight: 500;
  }
`;

const LegalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  margin-top: 24px;
  padding-bottom: 24px;
`;

const LegalLink = styled.a`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const LegalSeparator = styled.span`
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0 16px;
  width: 100%;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
`;

const DividerText = styled.span`
  padding: 0 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
  font-weight: 500;
`;

const JoinCodeButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  margin-bottom: 12px;
  border: 2px solid ${({ theme }) => theme.colors.accent};
  border-radius: 50px;
  background: white;
  color: ${({ theme }) => theme.colors.accent};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.accent}10;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PinButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  margin-bottom: 12px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50px;
  background: white;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary}10;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CirclePreviewCard = styled.div`
  margin: 8px 0 20px;
  padding: 20px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.surface || '#f9fafb'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
`;

const CircleName = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 4px;
  color: ${({ theme }) => theme.colors.text};
`;

const CircleMeta = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const OrText = styled.div`
  text-align: center;
  margin: 12px 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50px;
  background: white;
  color: ${({ theme }) => theme.colors.text};
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
